import React, { forwardRef, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select } from "antd";
import moment from "moment";
import debounce from "lodash.debounce";
import {
  GET_LOCAL_SUPPLIER_LISTS,
  GET_BUYER_ENDPOINT,
  GET_USERS_ENDPOINT,
  GET_ORGANIZATION_ENDPOINT,
  BOOKING_CARTON_ADD,
  BOOKING_CARTON_UPDATE,
  BOOKING_CARTON_GET_UNIQUE_SUPPLIER_PO,
  BOOKING_CARTON_UPLOAD_DOCUMENT,
  BOOKING_CARTON_SUPPORTING_MATERIAL_ADD,
} from "../../../../apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "../../../../apiServices/common";
import { alertPop } from "../../../../apiServices/common/helper";
import CustomDatePicker from "common/CustomDatePicker";
import CartonBookingUploadDoc from "../CartonBookingUploadDoc";
import BreakdownTable, { stripCartonBoxExcludedFields, stripPolyExcludedFields } from "./BreakdownTable";

const DEFAULT_DELIVERY_TO = "Concorde Garments Ltd";
const ORGANIZATION_DROPDOWN_PAGE_SIZE = 500;
const SHIPPING_MARK_OPTIONS = [
  { label: "Vertical", value: "Vertical" },
  { label: "Horizontal", value: "Horizontal" },
];
const DEFAULT_ORDER_BREAKDOWN = "Vertical";
const STATUS_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const roundBreakdownMoney = (n) => Number(Number(n || 0).toFixed(4));

const sizesMatch = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const isCartonBoxBreakdownRow = (row) => {
  const label = (row?.item_type_name || row?.item_category?.name || "").toLowerCase();
  return label.includes("carton box");
};

const isPolyBreakdownRow = (row) => {
  const label = (row?.item_type_name || row?.item_category?.name || "").toLowerCase();
  return label.includes("poly");
};

const resolveBreakdownQty = (row, sd, cartonBox) => {
  if (cartonBox) return null;
  return sd?.qty_pcs_dzn ?? sd?.quantity ?? row?.qty_pcs_dzn ?? row?.quantity ?? 0;
};

const getApiBreakdownGroupKey = (b) =>
  [
    b.sales_contract_id,
    b.po_map_id,
    b.style_id,
    b.sub_style_id,
    b.item_category_id,
    b.carton_type_id,
    b.ply_id,
    b.poly_type_id,
    b.gusset_type_id,
    b.air_hole_type_id,
    b.fabre_color_id,
    b.au_formula_id,
    b.unit_id,
    b.style_description,
    b.color_code,
    b.group,
    b.flap,
    b.flap_length,
    b.gusset,
    b.gusset_size,
    b.thickness,
    b.poly_print,
    b.hanger_cut,
    b.hanger_cut_style,
    b.hanger_gap_size,
    b.poly_perforation,
    b.poly_total_qty,
    b.note,
    b.article_no,
    b.length,
    b.width,
    b.height,
    b.additional_length,
    b.additional_width,
    b.rate,
  ].join("|");

const mapApiBreakdownToSizeDetail = (b, cartonBox) => {
  const additionalCost = Number(b.additional_cost) || 0;
  const lineTotal = Number(b.total_amount) || 0;
  const lineGrandTotal = lineTotal + additionalCost;
  const cartonQty = Number(b.carton_total_qty) || 0;
  const additionalChargePerPcs =
    cartonQty > 0 && additionalCost > 0 ? roundBreakdownMoney(additionalCost / cartonQty) : 0;
  return {
    id: b.id,
    size_id: b.size_id,
    size_name: b.size?.name,
    item: b.item_no ?? "",
    upc: b.upc ?? b.item_no ?? "",
    quantity: cartonBox ? null : b.quantity,
    carton_qty: b.carton_total_qty ?? b.quantity,
    carton_total_qty: b.carton_total_qty ?? b.quantity,
    garment_qty: b.garment_qty,
    qty_pcs_dzn: cartonBox ? null : b.qty_pcs_dzn ?? b.quantity,
    pack_qty: b.pack_qty ?? 1,
    rate_pcs_dzn: b.rate_qty,
    rate_pcs: b.rate_qty,
    rate_dzn: roundBreakdownMoney(Number(b.rate_qty || 0) * 12),
    rate: b.rate,
    total_cost: lineTotal,
    additional_cost: additionalCost,
    additional_charge_per_pcs: additionalChargePerPcs,
    grand_total: lineGrandTotal,
  };
};

const mapApiBreakdownToRow = (b) => {
  const itemTypeName = b.item_category?.name ?? "";
  const cartonBox = isCartonBoxBreakdownRow({ item_type_name: itemTypeName, item_category: b.item_category });
  const sizeDetail = mapApiBreakdownToSizeDetail(b, cartonBox);
  return {
    id: b.id,
    booking_carton_id: b.booking_carton_id,
    sales_contract_id: b.sales_contract_id,
    sales_contract_ref: b.sales_contract
      ? { reference_no: b.sales_contract.reference_no ?? b.sales_contract.sales_contract_unique_id }
      : undefined,
    po: b.po_map_id,
    po_map_id: b.po_map_id,
    po_map: b.po_map
      ? {
          id: b.po_map.id ?? b.po_map_id,
          original_po: b.po_map.original_po,
          internal_po: b.po_map.internal_po,
          delivery_date: b.po_map.delivery_date,
        }
      : undefined,
    style: b.style_id,
    style_id: b.style_id,
    sub_style_id: b.sub_style_id ?? b.sub_style?.id ?? b.subStyle?.id ?? null,
    sub_style: b.sub_style?.sub_style_no ?? b.subStyle?.sub_style_no ?? undefined,
    style_ref: b.style
      ? {
          style_no: b.style.style_no,
          description: b.style.description,
          brand_info: b.style.brand_info ?? b.style.brand,
        }
      : undefined,
    item_category_id: b.item_category_id,
    item_type: b.item_category_id,
    item_type_name: itemTypeName,
    item_category: b.item_category,
    carton_type_id: b.carton_type_id,
    ply_id: b.ply_id,
    poly_type_id: b.poly_type_id,
    gusset_type_id: b.gusset_type_id,
    air_hole_type_id: b.air_hole_type_id,
    fabre_color_id: b.fabre_color_id,
    au_formula_id: b.au_formula_id,
    unit_id: b.unit_id,
    carton_type: b.carton_type_id,
    ply: b.ply_id,
    poly_type: b.poly_type_id,
    gusset_type: b.gusset_type_id,
    air_hole: b.air_hole_type_id,
    au: b.au_formula_id,
    unit: b.unit_id,
    brand: b?.style?.brand_info?.name ?? b?.style?.brand?.name ?? undefined,
    style_description: b.style_description,
    color_code: b.color_code,
    group: b.group,
    flap: b.flap,
    flap_length: b.flap_length ?? (b.additional_length != null ? Number(b.additional_length) * 2 : null),
    flap_width: b.flap_width ?? (b.additional_width != null ? Number(b.additional_width) * 2 : null),
    gusset: b.gusset,
    gusset_size: b.gusset_size,
    thickness: b.thickness,
    poly_print: b.poly_print,
    poly_print_color: b.poly_print,
    hanger_cut: b.hanger_cut,
    hanger_cut_style: b.hanger_cut_style,
    hanger_cut_size: b.hanger_cut_size,
    hanger_gap_size: b.hanger_gap_size,
    poly_perforation: b.poly_perforation,
    poly_total_qty: b.poly_total_qty,
    note: b.note,
    item_no: b.item_no,
    artikel_nr: b.article_no,
    article_no: b.article_no,
    garment_qty: b.garment_qty,
    quantity: cartonBox ? null : b.quantity,
    qty_pcs_dzn: cartonBox ? null : b.qty_pcs_dzn ?? b.quantity,
    carton_total_qty: b.carton_total_qty,
    length: b.length,
    width: b.width,
    height: b.height,
    additional_length: b.additional_length,
    additional_width: b.additional_width,
    l: b.length,
    w: b.width,
    h: b.height,
    farbe: undefined,
    rate: b.rate,
    rate_qty: b.rate_qty,
    rate_pcs_doz: b.rate_qty,
    total_amount: sizeDetail.total_cost,
    total_cost: sizeDetail.total_cost,
    additional_cost: sizeDetail.additional_cost,
    grand_total: sizeDetail.grand_total,
    shipment_date: b.shipment_date ? moment(b.shipment_date) : undefined,
    size_details: [sizeDetail],
  };
};

/** API stores one breakdown per size; merge into drawer rows with multiple size_details. */
const groupApiBreakdownsForEdit = (breakdowns) => {
  const groups = new Map();
  (breakdowns || []).forEach((b) => {
    const key = getApiBreakdownGroupKey(b);
    if (!groups.has(key)) {
      groups.set(key, mapApiBreakdownToRow(b));
      return;
    }
    const row = groups.get(key);
    const cartonBox = isCartonBoxBreakdownRow({
      item_type_name: row.item_type_name,
      item_category: row.item_category,
    });
    const nextDetail = mapApiBreakdownToSizeDetail(b, cartonBox);
    const alreadyHas = row.size_details.some(
      (sd) =>
        (nextDetail.id != null && sd.id === nextDetail.id) ||
        sizesMatch(sd.size_id, nextDetail.size_id),
    );
    if (!alreadyHas) {
      row.size_details.push(nextDetail);
    }
  });

  const dedupeGroupedSizeDetails = (sizeDetails) => {
    const seen = new Set();
    return (sizeDetails || []).filter((sd) => {
      const dedupeKey = sd?.id != null ? `id:${sd.id}` : `size:${sd?.size_id ?? "__null__"}`;
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    });
  };

  return Array.from(groups.values()).map((row) => {
    const sizeDetails = dedupeGroupedSizeDetails(row.size_details || []);
    const subtotal = sizeDetails.reduce((sum, sd) => sum + (Number(sd.total_cost) || 0), 0);
    const additional = sizeDetails.reduce((sum, sd) => sum + (Number(sd.additional_cost) || 0), 0);
    const grandTotal = sizeDetails.reduce((sum, sd) => sum + (Number(sd.grand_total) || 0), 0);
    return {
      ...row,
      size_details: sizeDetails,
      id: sizeDetails[0]?.id ?? row.id,
      garment_qty: sizeDetails.reduce((sum, sd) => sum + (Number(sd.garment_qty) || 0), 0),
      carton_total_qty: sizeDetails.reduce((sum, sd) => sum + (Number(sd.carton_total_qty) || 0), 0),
      total_cost: roundBreakdownMoney(subtotal),
      additional_cost: roundBreakdownMoney(additional),
      grand_total: roundBreakdownMoney(grandTotal),
      total_amount: roundBreakdownMoney(subtotal),
    };
  });
};

const resolveOrganizationByName = (orgs, name) => {
  if (!name || !orgs?.length) return null;
  const normalized = name.trim().toLowerCase();
  return (
    orgs.find((o) => o.name === name) ||
    orgs.find((o) => o.name?.trim().toLowerCase() === normalized) ||
    null
  );
};

const resolveDefaultDeliveryOrganization = (orgs) => {
  if (!orgs?.length) return null;
  return (
    resolveOrganizationByName(orgs, DEFAULT_DELIVERY_TO) ||
    orgs.find((o) => /concorde\s+garments/i.test(o.name || "")) ||
    null
  );
};

const getOrganizationAddress = (org) => org?.address ?? null;

const setDeliveryFromOrganizationList = (form, deliveryTo, orgs) => {
  if (!deliveryTo) {
    form.setFieldsValue({ delivery_address: undefined });
    return;
  }
  const org = resolveOrganizationByName(orgs, deliveryTo);
  form.setFieldsValue({
    delivery_address: org ? getOrganizationAddress(org) : undefined,
  });
};

const applyDefaultDeliveryOrganization = (form, orgs) => {
  const defaultOrg = resolveDefaultDeliveryOrganization(orgs);
  if (!defaultOrg) return;
  form.setFieldsValue({
    delivery_to: defaultOrg.name,
    delivery_address: getOrganizationAddress(defaultOrg),
  });
};

const ReconciliationForm = forwardRef((props) => {
  const {
    reconciliationInfo,
    form,
    discartForm,
    isEdit,
    viewMode,
    reconciliationId,
    onCreated,
    onSupportingMaterialAdded,
  } = props;

  const [supplierList, setSupplierList] = useState([]);
  const [supplierPoNumber, setSupplierPoNumber] = useState("");
  const [filesList, setFileList] = useState([]);
  const [cglContractPersonList, setCglContractPersonList] = useState([]);
  const [buyerList, setBuyerList] = useState([]);
  const [buyerLoading, setBuyerLoading] = useState(false);
  const [organizationList, setOrganizationList] = useState([]);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const organizationListRef = useRef([]);
  const watchedBuyerId = Form.useWatch("buyer_id", form);
  const watchedDeliveryTo = Form.useWatch("delivery_to", form);

  const fetchOrganizationList = useCallback(async (filterValues = {}) => {
    setOrganizationLoading(true);
    const response = await getData(GET_ORGANIZATION_ENDPOINT, false, {
      per_page: ORGANIZATION_DROPDOWN_PAGE_SIZE,
      ...filterValues,
    });
    let orgs = [];
    if (response?.data?.code === 200) {
      orgs = response?.data?.data?.data || [];
      setOrganizationList(orgs);
      organizationListRef.current = orgs;
    }
    setOrganizationLoading(false);
    return orgs;
  }, []);

  useEffect(() => {
    fetchSuppliers();
    fetchCglContractPersonList();
    fetchBuyerList({ per_page: 10 });
    fetchOrganizationList();

    if (!isEdit && !viewMode) {
      fetchSupplierPoNumber();
      form.setFieldsValue({
        date: moment(),
        status: "Active",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!organizationList.length) return;

    if (!isEdit && !viewMode && !reconciliationInfo) {
      const currentDeliveryTo = form.getFieldValue("delivery_to");
      if (!currentDeliveryTo) {
        applyDefaultDeliveryOrganization(form, organizationList);
        return;
      }
    }

    if (watchedDeliveryTo) {
      setDeliveryFromOrganizationList(form, watchedDeliveryTo, organizationList);
    }
  }, [organizationList, watchedDeliveryTo, isEdit, viewMode, reconciliationInfo, form]);

  useEffect(() => {
    if (reconciliationInfo) {
      const buyingOfficeId = reconciliationInfo?.buying_office?.id ?? reconciliationInfo?.buying_office_id;
      const buyerId = reconciliationInfo?.buyer?.id ?? reconciliationInfo?.buyer_id;
      const deliveryTo = reconciliationInfo?.delivery_to ?? DEFAULT_DELIVERY_TO;
      form.setFieldsValue({
        supplier_po_number: reconciliationInfo?.supplier_po_number ?? supplierPoNumber,
        date: reconciliationInfo?.booking_date ? moment(reconciliationInfo.booking_date) : moment(),
        supplier_id: reconciliationInfo?.supplier_id,
        buyer_id: buyerId,
        buying_office_id: buyingOfficeId,
        sub_contract_factory_id:
          reconciliationInfo?.sub_contract_factory?.id ?? reconciliationInfo?.sub_contract_factory_id ?? null,
        contract_person: reconciliationInfo?.contract_person,
        contract_person_address: reconciliationInfo?.contract_person_address,
        phone: reconciliationInfo?.contract_person_phone,
        delivery_to: deliveryTo,
        cgl_contract_person: reconciliationInfo?.cgl_poc_id ?? reconciliationInfo?.cgl_poc?.id,
        cgl_contract_person_phone:
          reconciliationInfo?.cgl_poc_phone ?? reconciliationInfo?.cgl_contract_person_phone ?? null,
        shipping_mark: reconciliationInfo?.order_breakdown ?? DEFAULT_ORDER_BREAKDOWN,
        status: reconciliationInfo?.status ?? "Active",
      });
      if (reconciliationInfo?.buyer) {
        setBuyerList((prev) => {
          if (prev.some((b) => b.id === reconciliationInfo.buyer.id)) return prev;
          return [reconciliationInfo.buyer, ...prev];
        });
      }
      if (buyerId) {
        onBuyerSelect(buyerId);
      }
      if (reconciliationInfo?.supplier_po_number) {
        setSupplierPoNumber(reconciliationInfo.supplier_po_number);
      }
      if (reconciliationInfo?.documents?.length) {
        const mappedDocs = reconciliationInfo.documents.map((doc) => ({
          id: doc.id,
          name: doc.document_name,
          path: doc.document_path || doc.document,
        }));
        setFileList(mappedDocs);
      } else {
        setFileList([]);
      }
      if (reconciliationInfo?.breakdowns?.length) {
        const breakdownDetails = groupApiBreakdownsForEdit(reconciliationInfo.breakdowns);
        form.setFieldValue("breakdown_details", breakdownDetails);
      }
    }
  }, [reconciliationInfo]);

  const fetchSuppliers = async () => {
    const response = await getData(GET_LOCAL_SUPPLIER_LISTS, false, {});
    if (response?.data?.code === 200) {
      setSupplierList(response?.data?.data || []);
    }
  };

  const fetchBuyerList = async (filterValues = {}) => {
    setBuyerLoading(true);
    const response = await getData(GET_BUYER_ENDPOINT, false, { per_page: 10, ...filterValues });
    if (response?.data?.code === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
    setBuyerLoading(false);
  };

  const debouncedFetchBuyers = useMemo(
    () => debounce((name) => fetchBuyerList(name ? { name } : { per_page: 10 }), 400),
    [],
  );

  const debouncedFetchOrganizations = useMemo(
    () =>
      debounce(
        (name) =>
          fetchOrganizationList(
            name ? { name, per_page: 10 } : { per_page: ORGANIZATION_DROPDOWN_PAGE_SIZE },
          ),
        400,
      ),
    [fetchOrganizationList],
  );

  const fetchCglContractPersonList = async (searchValue = "") => {
    const bodyData = {
      per_page: 10,
      department_id: 3,
      ...(searchValue && { name: searchValue }),
    };
    const response = await getData(GET_USERS_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200 || response?.status === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setCglContractPersonList(data);
    }
  };

  const onSearchCglContractPerson = (searchValue) => {
    fetchCglContractPersonList(searchValue);
  };

  const onCglContractPersonChange = (userId) => {
    const user = cglContractPersonList.find((u) => u.id === userId);
    form.setFieldsValue({ cgl_contract_person_phone: user?.phone ?? null });
  };

  const fetchSupplierPoNumber = async () => {
    try {
      const response = await getData(BOOKING_CARTON_GET_UNIQUE_SUPPLIER_PO);
      if (response?.data?.code === 200 && response?.data?.data != null) {
        const value = typeof response.data.data === "string" ? response.data.data : (response.data.data?.po ?? "");
        setSupplierPoNumber(value);
        form.setFieldsValue({ supplier_po_number: value });
      } else {
        form.setFieldsValue({ supplier_po_number: "" });
      }
    } catch {
      form.setFieldsValue({ supplier_po_number: "" });
    }
  };

  const onBuyerSelect = async (buyerId) => {
    if (!buyerId) {
      form.setFieldsValue({
        contract_person_address: undefined,
        buying_office_id: undefined,
        sub_contract_factory_id: undefined,
      });
      return;
    }
    try {
      const response = await getData(`/api/buyers/${buyerId}/edit`);
      const buyer = response?.data?.data;
      if (buyer) {
        form.setFieldsValue({
          contract_person_address: buyer.address ?? null,
        });
      }
    } catch {
      form.setFieldsValue({ contract_person_address: undefined });
    }
  };

  const onDeliveryOrganizationChange = (orgName) => {
    setDeliveryFromOrganizationList(form, orgName, organizationListRef.current);
  };

  const buildBreakdownsPayload = (breakdown_details) => {
    const breakdowns = [];
    (breakdown_details || []).forEach((rawRow) => {
      const row = stripPolyExcludedFields(
        stripCartonBoxExcludedFields(rawRow, () => rawRow?.item_type_name || rawRow?.item_category?.name || ""),
        () => rawRow?.item_type_name || rawRow?.item_category?.name || "",
      );
      const articleNo = row.article_no ?? row.artikel_nr ?? null;
      const sizeDetails = row.size_details || [];
      const itemCategoryId = row.item_category_id ?? row.item_type ?? null;
      const cartonBox = isCartonBoxBreakdownRow(row);
      const polyBag = isPolyBreakdownRow(row);
      const normalizedSizeDetails = sizeDetails.map((sd) => ({
        size_id: sd.size_id ?? row.size_id ?? null,
        item: cartonBox || polyBag ? null : sd.item ?? row.item_no ?? row.item ?? "",
        upc: cartonBox || polyBag ? null : sd.upc ?? row.upc ?? row.item_no ?? "",
        garment_qty: sd.garment_qty ?? row.garment_qty ?? 0,
        qty_pcs_dzn: resolveBreakdownQty(row, sd, cartonBox),
        pack_qty: sd.pack_qty ?? row.pack_qty ?? 1,
        carton_qty: sd.carton_qty ?? sd.quantity ?? row.carton_total_qty ?? row.quantity ?? 0,
      }));

      const buildBreakdownEntry = (sd, sdIndex) => ({
        id: sd?.id ?? 0,
        booking_carton_id: row.booking_carton_id,
        sales_contract_id: row.sales_contract_id ?? null,
        po_map_id: row.po_map_id ?? row.po,
        style_id: row.style_id ?? row.style,
        sub_style_id: row.sub_style_id ?? null,
        item_category_id: itemCategoryId,
        carton_type_id: row.carton_type_id ?? row.carton_type ?? null,
        ply_id: row.ply_id ?? row.ply ?? null,
        poly_type_id: cartonBox ? null : row.poly_type_id ?? row.poly_type ?? null,
        gusset_type_id: cartonBox
          ? null
          : row.gusset_type_id ?? (Array.isArray(row.gusset_type) ? row.gusset_type?.[0] : row.gusset_type) ?? null,
        air_hole_type_id: cartonBox ? null : row.air_hole_type_id ?? row.air_hole ?? null,
        fabre_color_id: row.fabre_color_id ?? null,
        au_formula_id: row.au_formula_id ?? row.au ?? null,
        unit_id: cartonBox ? null : row.unit_id ?? row.unit ?? null,
        size_id: (sd?.size_id ?? row.size_id) ?? null,
        style_description: row.style_description ?? null,
        color_code: row.color_code ?? null,
        group: row.group ?? null,
        flap: cartonBox ? null : row.flap ?? null,
        flap_length: cartonBox ? null : row.flap_length ?? null,
        gusset: cartonBox ? null : row.gusset ?? null,
        gusset_size: cartonBox ? null : row.gusset_size ?? null,
        thickness: cartonBox ? null : row.thickness ?? null,
        poly_print: cartonBox ? null : row.poly_print ?? row.poly_print_color ?? null,
        hanger_cut: cartonBox ? null : row.hanger_cut ?? null,
        hanger_cut_style: cartonBox ? null : row.hanger_cut_style ?? null,
        hanger_cut_size: cartonBox ? null : row.hanger_cut_size ?? null,
        hanger_gap_size: cartonBox ? null : row.hanger_gap_size ?? null,
        poly_perforation: cartonBox ? null : row.poly_perforation ?? null,
        poly_total_qty: cartonBox ? 0 : row.poly_total_qty ?? null,
        note: row.note ?? null,
        item: cartonBox ? null : sd?.item ?? row.item ?? row.item_no ?? null,
        item_no: sd?.item ?? row.item_no ?? row.item ?? null,
        upc: cartonBox ? null : sd?.upc ?? row.upc ?? row.item_no ?? "",
        article_no: articleNo,
        garment_qty: sd?.garment_qty ?? row.garment_qty ?? null,
        quantity: resolveBreakdownQty(row, sd ?? row, cartonBox),
        carton_total_qty: sd?.carton_qty ?? row.carton_total_qty ?? null,
        length: row.length ?? row.l ?? null,
        width: row.width ?? row.w ?? null,
        height: row.height ?? row.h ?? null,
        additional_length: polyBag ? row.additional_length ?? 0 : 0,
        additional_width: polyBag ? row.additional_width ?? 0 : 0,
        rate: sd?.rate ?? row.rate ?? null,
        rate_qty: sd?.rate_pcs ?? sd?.rate_pcs_dzn ?? row.rate_qty ?? row.rate_pcs_doz ?? null,
        additional_cost:
          sd?.additional_cost != null && sd?.additional_cost !== ""
            ? Number(sd.additional_cost)
            : row.additional_cost != null && row.additional_cost !== ""
              ? Number(row.additional_cost)
              : null,
        total_amount: sd?.total_cost ?? row.total_amount ?? row.total_cost ?? null,
        size_details: sd
          ? [
              {
                size_id: sd.size_id ?? row.size_id ?? null,
                item: cartonBox || polyBag ? null : sd.item ?? row.item_no ?? row.item ?? "",
                upc: cartonBox || polyBag ? null : sd.upc ?? row.upc ?? row.item_no ?? "",
                garment_qty: sd.garment_qty ?? row.garment_qty ?? 0,
                qty_pcs_dzn: resolveBreakdownQty(row, sd, cartonBox),
                pack_qty: sd.pack_qty ?? row.pack_qty ?? 1,
                carton_qty: sd.carton_qty ?? sd.quantity ?? row.carton_total_qty ?? row.quantity ?? 0,
              },
            ]
          : normalizedSizeDetails,
        created_by: row.created_by ?? null,
        updated_by: row.updated_by ?? null,
        deleted_by: row.deleted_by ?? null,
      });

      if (sizeDetails.length === 0) {
        breakdowns.push(buildBreakdownEntry(row, 0));
      } else {
        sizeDetails.forEach((sd, sdIndex) => {
          breakdowns.push(buildBreakdownEntry(sd, sdIndex));
        });
      }
    });
    return breakdowns;
  };

  const onFinish = async (values) => {
    const breakdowns = buildBreakdownsPayload(values?.breakdown_details || []);
    const documents = (filesList || []).filter((f) => f.id).map((doc) => ({ id: doc.id, is_delete: 0 }));

    const bodyData = {
      buying_office_id: values.buying_office_id ?? null,
      buyer_id: values.buyer_id ?? null,
      sub_contract_factory_id: values.sub_contract_factory_id ?? null,
      supplier_po_number: values.supplier_po_number,
      supplier_id: values.supplier_id,
      booking_date: values?.date ? moment(values.date).format("YYYY-MM-DD") : null,
      contract_person: values.contract_person ?? null,
      contract_person_phone: values.phone ?? null,
      contract_person_address: values.contract_person_address ?? null,
      delivery_to: values.delivery_to ?? null,
      cgl_poc_id: values.cgl_contract_person,
      cgl_poc_phone: values.cgl_contract_person_phone ?? null,
      order_breakdown: values.shipping_mark ?? null,
      status: values.status ?? "Active",
      documents,
      breakdowns,
    };

    if (reconciliationId) {
      const response = await putData(BOOKING_CARTON_UPDATE(reconciliationId), bodyData);
      if (response?.data?.code === 200 || response?.code === 200) {
        alertPop("success", "Carton & Poly booking updated successfully");
        discartForm();
      } else {
        alertPop("error", response?.data?.message?.[0] || response?.data?.message || "Update failed");
      }
    } else {
      const response = await postData(BOOKING_CARTON_ADD, bodyData);
      if (response?.code === 200 || response?.data?.code === 200) {
        const createdId = response?.data?.id ?? response?.data?.data?.id ?? response?.data?.booking?.id ?? response?.id;
        alertPop("success", "Carton & Poly booking created successfully");
        if (onCreated && createdId) {
          onCreated(createdId);
        } else {
          form.resetFields();
          discartForm();
        }
      } else {
        alertPop("error", response?.data?.message?.[0] || response?.data?.message || "Create failed");
      }
    }
  };

  const handleSaveSupportingMaterial = async (bookingCartonId, payload) => {
    const response = await postData(BOOKING_CARTON_SUPPORTING_MATERIAL_ADD(bookingCartonId), payload);
    if (response?.code === 200) {
      alertPop("success", "Supporting Material Added Successfully");
      if (onSupportingMaterialAdded) onSupportingMaterialAdded();
    } else {
      alertPop("error", response?.data?.message?.[0] || "Failed to add supporting material");
    }
    return response;
  };

  return (
    <div className="containt-body pt-3">
      <Form
        layout="vertical"
        form={form}
        name="carton-booking-form"
        onFinish={onFinish}
        initialValues={{
          date: moment(),
          status: "Active",
          shipping_mark: DEFAULT_ORDER_BREAKDOWN,
          breakdown_details: [],
        }}
      >
        <Card title="Carton & Poly Booking">
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Supplier PO Number" name="supplier_po_number">
                <Input placeholder="Auto Generated" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Booking Date" name="date" rules={[{ required: true, message: "Date is required" }]}>
                <CustomDatePicker style={{ width: "100%" }} size="small" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                label="Supplier"
                name="supplier_id"
                rules={[{ required: true, message: "Supplier is required" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="From ERP"
                  disabled={viewMode || isEdit}
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label ?? option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                  }
                  options={supplierList.map((s) => ({ label: s.name, value: s.id }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                label="Buyer"
                name="buyer_id"
                rules={[{ required: true, message: "Buyer is required" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search buyer"
                  disabled={viewMode || isEdit}
                  style={{ width: "100%" }}
                  loading={buyerLoading}
                  filterOption={false}
                  onSearch={debouncedFetchBuyers}
                  onClear={() => fetchBuyerList({ per_page: 10 })}
                  onChange={onBuyerSelect}
                  options={buyerList.map((b) => ({ label: b.name, value: b.id }))}
                />
              </Form.Item>
            </Col>
            <Form.Item name="buying_office_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sub_contract_factory_id" hidden>
              <Input />
            </Form.Item>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Contact Person (Supplier)" name="contract_person">
                <Input placeholder="Contact Person (Supplier)" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Address" name="contract_person_address">
                <Input placeholder="Buyer address" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Contact Person Phone (Supplier)" name="phone">
                <Input placeholder="Phone (Supplier)" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Delivery To" name="delivery_to">
                <Select
                  showSearch
                  allowClear
                  placeholder="Delivery To"
                  disabled={viewMode}
                  style={{ width: "100%" }}
                  loading={organizationLoading}
                  filterOption={false}
                  onSearch={debouncedFetchOrganizations}
                  onClear={() => fetchOrganizationList({ per_page: ORGANIZATION_DROPDOWN_PAGE_SIZE })}
                  onChange={onDeliveryOrganizationChange}
                  options={organizationList.map((org) => ({
                    label: org.name,
                    value: org.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Delivery Address" name="delivery_address">
                <Input placeholder="Delivery Address" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                label="Contact Person (CGL)"
                name="cgl_contract_person"
                rules={[{ required: true, message: "Contact Person (CGL) is required" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Contact Person (CGL)"
                  disabled={viewMode}
                  style={{ width: "100%" }}
                  filterOption={false}
                  onSearch={onSearchCglContractPerson}
                  onChange={onCglContractPersonChange}
                  options={cglContractPersonList.map((user) => ({ label: user.name, value: user.id }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Contact Person Phone (CGL)" name="cgl_contract_person_phone">
                <Input placeholder="Phone (CGL)" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Order Breakdown" name="shipping_mark">
                <Select
                  placeholder="Vertical/Horizontal"
                  allowClear
                  disabled={viewMode}
                  style={{ width: "100%" }}
                  options={SHIPPING_MARK_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Status" name="status">
                <Select
                  placeholder="Status"
                  allowClear
                  disabled={viewMode}
                  style={{ width: "100%" }}
                  options={STATUS_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <CartonBookingUploadDoc
          DOC_ADD={BOOKING_CARTON_UPLOAD_DOCUMENT}
          setFileList={setFileList}
          filesList={filesList}
          view={viewMode}
          disableUpdateButton={false}
          disableField={false}
        />

        <Card title="Breakdown Details" className="mt-3">
          <BreakdownTable
            form={form}
            viewMode={viewMode}
            buyerId={watchedBuyerId}
            isEdit={isEdit}
            bookingCartonId={reconciliationId}
            onSaveSupportingMaterial={handleSaveSupportingMaterial}
            supportingMaterials={
              reconciliationInfo?.supporting_materials ?? reconciliationInfo?.supportingMaterials ?? []
            }
          />
        </Card>
      </Form>
    </div>
  );
});

ReconciliationForm.displayName = "ReconciliationForm";

export default ReconciliationForm;
